import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { extractPdfData } from '../api/pdfApi';
import { FaFileUpload, FaFilePdf, FaTimes } from 'react-icons/fa';
import '../styles/transitions.css';

// Page container style for centering
const pageContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-7vh'
};

// Dropzone styling
const dropzoneStyle = {
  border: '2px dashed #dee2e6',
  borderRadius: '8px',
  padding: '3rem 2rem',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: '#f8f9fa'
};

const dropzoneActiveStyle = {
  ...dropzoneStyle,
  borderColor: 'var(--primary)',
  backgroundColor: '#e7f1ff'
};

const dropzoneErrorStyle = {
  ...dropzoneStyle,
  borderColor: '#dc3545',
  backgroundColor: '#ffe7e7'
};

const filePreviewStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '6px',
  marginTop: '1rem'
};

const ApplicationUpload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Trigger fade-in animation
  React.useEffect(() => {
    setTimeout(() => setPageLoaded(true), 100);
  }, []);
  
  // Validate file
  const validateFile = (file) => {
    if (!file) {
      return 'Please select a file';
    }
    
    if (file.type !== 'application/pdf') {
      return 'Only PDF files are allowed';
    }
    
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
      return 'File size must be less than 15MB';
    }
    
    return null;
  };
  
  // Handle file selection
  const handleFileSelect = (file) => {
    const validationError = validateFile(file);
    
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }
    
    setSelectedFile(file);
    setError(null);
  };
  
  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  
  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);
  
  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a PDF file to scan');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Extract data from PDF
      const result = await extractPdfData(selectedFile);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to extract data from PDF');
      }
      
      // Navigate to the form with extracted data and the uploaded file
      navigate('/application/new', {
        state: {
          extractedData: result.opportunityData,
          applicationId: result.applicationId,
          validation: result.validation,
          uploadedApplicationFile: selectedFile // Pass the PDF file to auto-attach as application document
        }
      });
      
    } catch (err) {
      console.error('PDF extraction error:', err);
      setError(err.message || 'Failed to scan application. Please try again or enter data manually.');
      setLoading(false);
    }
  };
  
  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  return (
    <div className={`fade-in ${pageLoaded ? 'active' : ''}`} style={pageContainerStyle}>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={7} className="text-center">
            <Card className="shadow-lg border-0 slide-in-right">
              <Card.Body className="p-4">
                <h3 className="mb-4">Upload Application</h3>
                <p className="text-muted mb-4">
                  Upload a signed PDF application form to automatically extract business information
                </p>
                
                {error && (
                  <Alert variant="danger" className="slide-in-down" onClose={() => setError(null)} dismissible>
                    {error}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit} className="fade-in">
                  {/* File Upload Dropzone */}
                  <div
                    style={error ? dropzoneErrorStyle : (isDragging ? dropzoneActiveStyle : dropzoneStyle)}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !loading && document.getElementById('file-input').click()}
                  >
                    <FaFileUpload 
                      size={48} 
                      color={isDragging ? 'var(--primary)' : '#6c757d'} 
                      className="mb-3"
                    />
                    <h5 className="mb-2">
                      {isDragging ? 'Drop PDF here' : 'Drag and drop PDF here'}
                    </h5>
                    <p className="text-muted mb-0">or click to browse</p>
                    <p className="text-muted small mt-2">
                      Maximum file size: 15MB
                    </p>
                    
                    <input
                      id="file-input"
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      disabled={loading}
                    />
                  </div>
                  
                  {/* File Preview */}
                  {selectedFile && (
                    <div style={filePreviewStyle}>
                      <div className="d-flex align-items-center">
                        <FaFilePdf size={32} color="#dc3545" className="me-3" />
                        <div className="text-start">
                          <div className="fw-bold">{selectedFile.name}</div>
                          <div className="text-muted small">{formatFileSize(selectedFile.size)}</div>
                        </div>
                      </div>
                      {!loading && (
                        <Button 
                          variant="link" 
                          className="text-danger p-0"
                          onClick={handleRemoveFile}
                        >
                          <FaTimes size={20} />
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Submit Button */}
                  <div className="d-grid mt-4">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading || !selectedFile}
                      className="d-flex justify-content-center align-items-center py-2"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Scanning Application...
                        </>
                      ) : (
                        'Scan Application'
                      )}
                    </Button>
                  </div>
                  
                  {/* Manual Entry Option */}
                  <div className="mt-3">
                    <Button
                      variant="link"
                      onClick={() => navigate('/application/new')}
                      disabled={loading}
                      className="text-muted"
                    >
                      Or enter information manually
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ApplicationUpload;

